/**
 * 会员套餐 · 前端口径。
 *
 * 后台 `plans` 表是唯一权威（上限、VLM 次数都在那儿，配额校验 `check_quota`
 * 直接读它），这里**不重复存数值**，只做三件事：
 *
 *   1. 判定「这个用户是不是 Plus」—— 只认 `plan_id`，不认名字。名字会被后台改。
 *   2. 给界面一套固定的中文措辞（免费会员 / Plus 会员）。数据库里那列叫
 *      「免费版」，但站内文案统一称「会员」，两处不一致时以这里为准。
 *   3. 升级弹窗里的对比表。
 *
 * ⚠️ 对比表里每一行都必须是**当前真的能验证的差异**，别写"即将支持"：
 *    承诺了没做的功能，用户升级完第一件事就是来问。新增差异时先确认代码里
 *    确实拦住了，再往表里加。
 */
import { CurrentUser } from './api'

/** 与 plans.id 一致。改成别的 id 要同时改 0010 迁移 */
export const PLAN_PLUS = 'plus'

export function isPlusUser(user: CurrentUser | null | undefined): boolean {
	// 未登录一律按免费处理：编辑页允许游客写谱，但 Plus 能力要账号
	return user?.plan_id === PLAN_PLUS
}

/** 曲谱数量上限。老响应没有 max_scores 时按免费档 20 兜底 */
export function scoreLimitOf(user: CurrentUser | null | undefined): number {
	const n = user?.max_scores
	return typeof n === 'number' && n > 0 ? n : 20
}

/** 会员标识文案（顶栏/库里那个小徽章、升级弹窗标题都用它） */
export function planLabel(user: CurrentUser | null | undefined): string {
	return isPlusUser(user) ? 'Plus 会员' : '免费会员'
}

/**
 * 历史版本**看得见**的条数（不是存储上限 —— 库里两档都留 20 条，只是免费用户
 * 只看得到最近 3 条，升级后旧版本还在、能直接回滚）。
 *
 * 切分点以后端 `views_scores.VERSION_VISIBLE` 为准，这里只负责显示文案。
 */
export const VERSION_VISIBLE_FREE = 3
export const VERSION_VISIBLE_PLUS = 15

export function versionLimitOf(user: CurrentUser | null | undefined): number {
	return isPlusUser(user) ? VERSION_VISIBLE_PLUS : VERSION_VISIBLE_FREE
}

/**
 * 细分项：同一功能下的具体开关（倍速、织体、对错统计…）。
 *
 * 这些文案**必须整行铺开**，不能塞进 84/96px 的两列里 —— 实测「0.1×–4.0×，
 * 步长 0.1」在 96px 会折成四行，右列参差不齐，看着像排版坏了。
 */
export type PlanFeatureDetail = {
	/** 细分项名称，如「倍速」 */
	label: string
	/** 具体内容，尽量短：一句一行 */
	text: string
	/** 归谁：plus = Plus 专属（默认）；both = 两档一致 */
	who?: 'plus' | 'both'
}

export type PlanFeatureRow = {
	/** 功能名 */
	name: string
	/** 免费会员：字符串=额度/状态，true/false=有无 */
	free: string | boolean
	/** Plus 会员：同上 */
	plus: string | boolean
	/** 该功能下的细分差异，渲染成紧跟其后的整行小字 */
	details?: PlanFeatureDetail[]
}

/**
 * 升级弹窗的对比表。口径来自《会员与非会员功能对照.md》。
 *
 * 顺序照产品给的清单走：先「有多少」（曲谱/历史版本），再「能导出什么」，
 * 再「练琴四件套」（试听/视弹/编辑/跟弹/伴奏）。别按字母或按开发时间排。
 *
 * 「试听 / 视弹」两行写成「浮窗版 / 控制台」是实话，不是话术：免费用户从
 * 浮窗菜单点试听、视弹是**能用的**（起播/暂停/开麦判定，不开面板），而完整
 * 控制台挂在「更多」里、「更多」是 Plus 功能 —— 所以细分项（倍速、章节、
 * 节拍模式…）只有 Plus 摸得到。
 */
export const PLAN_FEATURES: PlanFeatureRow[] = [
	{name: '曲谱数量', free: '20 首', plus: '200 首'},
	{name: '历史版本（可回滚）', free: '3 个历史版本', plus: '15 个历史版本', details: [
		{label: '回滚', text: '内容有变化才记一版，可回滚到其中任意一版', who: 'both'},
		{label: '旧版本', text: '超出条数的不会被删除，升级后仍能回滚', who: 'both'},
	]},
	{name: '导出简谱 / 五线谱', free: true, plus: true},
	{name: '导入 MusicXML 生成简谱', free: true, plus: true},
	{name: '去水印', free: false, plus: true, details: [
		{label: '涉及', text: '预览叠层、导出 PDF、五线谱 PDF 三处', who: 'both'},
	]},
	{name: '试听（播放曲谱）', free: '浮窗版', plus: '控制台', details: [
		{label: '倍速', text: '0.1×–4.0×，步长 0.1'},
		{label: '章节', text: '按章节切换，或在预览里点选小节定位'},
		{label: '节拍', text: '节拍器可选不同音色（节拍 1 / 节拍 2 / 军鼓 / 鼓点）'},
		{label: '反复', text: '设置反复次数，或忽略反复与区分'},
	]},
	{name: '视弹（逐音判定）', free: '浮窗版', plus: '控制台', details: [
		{label: '自由节奏', text: '弹对就推进，不限时', who: 'both'},
		{label: '节拍模式', text: '按时间轴亮灯，命中保留、错过熄灭'},
		{label: '节拍节奏', text: '开关 + 音量 0–150（音色固定军鼓）'},
		{label: '节拍器', text: '启用 / 关闭'},
		{label: '对错统计', text: '累计弹对数、弹错次数'},
	]},
	{name: '编辑曲谱', free: true, plus: true},
	{name: '跟弹（示范 → 回弹）', free: false, plus: true, details: [
		{label: '速度', text: '0.6× / 0.8× / 1× / 1.2×'},
		{label: '分段', text: '每段 1 / 2 / 4 小节交替示范与回弹'},
		{label: '声部', text: '主旋律 / 伴奏'},
		{label: '范围', text: '反复遍数 + 起始小节'},
		{label: '对错统计', text: '累计弹对数、弹错次数'},
	]},
	{name: '自动左手伴奏', free: false, plus: '自动生成', details: [
		{label: '难度', text: '极简 / 入门 / 进阶'},
		{label: '织体', text: '单音 / 根音+五度 / 分解和弦 / 流行抒情 / 华尔兹 / 民谣'},
		{label: '密度', text: '每小节一个 / 每小节两个'},
		{label: '和声', text: '标准 / 精细'},
		{label: '音色', text: '钢琴 / 吉他 / 风琴 / 小提琴 / 短笛 / 电子琴 / 军鼓'},
	]},
]

/** 被 Plus 锁挡住的具体入口 —— 弹窗标题会带出来，别让人猜「我点的是哪个」 */
export type PlusFeature = 'echo' | 'accompaniment' | 'inspector' | 'history' | 'generic'

export const PLUS_FEATURE_NAMES: Record<PlusFeature, string> = {
	echo: '跟弹练习',
	accompaniment: '自动左手伴奏',
	inspector: '完整练习面板',
	history: '更多历史版本',
	generic: '这个功能',
}
